/* eslint-disable @typescript-eslint/no-explicit-any */
import { APP_NAME } from '@/config';

const FINGERPRINT_KEY = `${APP_NAME}_FINGERPRINT_V1`;
// const FINGERPRINT_TTL_DAYS = 365;

export type DeviceInfo = {
  userAgent: string;
  language: {
    primary: string;
    all: string[];
  };
  addBlock: boolean;
  incognito: boolean;
  platform: string;
  hardwareConcurrency: number | null;
  deviceMemory: number | null;
  screen: {
    width: number;
    height: number;
    pixelRatio: number;
    colorDepth: number;
  };
  timezone: string;
  timezoneOffset: number;
  locale: string;
  connection: {
    type: string;
    effectiveType: string;
    downlink: number;
    rtt: number;
  };
  plugins: string[];
  gpu: string;
  webgl2Support: boolean;
  fingerprint: string;
};

/**
 * Retorna o fingerprint do cache (localStorage) de forma síncrona.
 * Usado no unload (sendBeacon) onde não podemos usar async.
 * Retorna '' se ainda não tiver sido calculado.
 */
export function getFingerprintSync(): string {
  if (typeof window === 'undefined') return '';
  return localStorage.getItem(FINGERPRINT_KEY) ?? '';
}

/**
 * Gera ou recupera o Fingerprint do device
 */
export async function getFingerprint(): Promise<string> {
  if (typeof window === 'undefined') return '';

  const cached = localStorage.getItem(FINGERPRINT_KEY);
  if (cached) return cached as string;

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  ctx?.fillText(APP_NAME, 10, 50);
  const canvasData = canvas.toDataURL();

  let gpuInfo = '';
  try {
    const gl = canvas.getContext('webgl') as WebGLRenderingContext | null;
    if (gl) {
      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
      if (debugInfo) {
        const vendor = gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL);
        const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
        gpuInfo = `${vendor} ${renderer}`;
      }
    }
  } catch {
    // eslint-disable-next-line no-empty
  }

  let pluginNames = '';
  try {
    pluginNames = Array.from(navigator.plugins || [])
      .map(p => p.name)
      .join(',');
  } catch {
    // eslint-disable-next-line no-empty
  }

  const timezoneOffset = new Date().getTimezoneOffset().toString();
  const locale = Intl.DateTimeFormat().resolvedOptions().locale;

  const components = [
    navigator.userAgent,
    navigator.language,
    navigator.platform,
    navigator.hardwareConcurrency?.toString() || '',
    (navigator as any).deviceMemory?.toString() || '',
    screen.width.toString(),
    screen.height.toString(),
    screen.colorDepth.toString(),
    Intl.DateTimeFormat().resolvedOptions().timeZone,
    timezoneOffset,
    locale,
    canvasData,
    window.devicePixelRatio?.toString() || '',
    (navigator as any).connection?.effectiveType || '',
    pluginNames,
    gpuInfo,
    typeof (window as any).WebGL2RenderingContext !== 'undefined'
      ? 'webgl2'
      : 'no-webgl2',
  ];

  const encoder = new TextEncoder();
  const dataBuffer = encoder.encode(components.join('|'));
  const hashBuffer = await crypto.subtle.digest('SHA-256', dataBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const fingerprint = hashArray
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');

  localStorage.setItem(FINGERPRINT_KEY, fingerprint);
  return fingerprint;
}
